import { useNavigate } from 'react-router-dom'
import EntryStatsRow from './EntryStatsRow'

/**
 * BracketEntryCard - A consistent card component for displaying bracket entries
 *
 * Props:
 * - bracketId: UUID of the bracket
 * - entryId: UUID of the user's entry (null if no entry)
 * - divisionName: Name of the division
 * - bracketStatus: Status of the bracket (draft, open, locked, complete)
 * - metadata: Object with { total_points, potential_points, accuracy, champion_status, champion_team_name? }
 *   — when champion_team_name is set, the champ's name replaces the Won/Eliminated label
 * - variant: 'default' | 'needsPicks' - controls styling
 * - blurVariant: 0-5 for per-card blur shape variety (default from bracketId)
 * - onClick: Optional custom click handler (overrides default navigation)
 */
export default function BracketEntryCard({
  bracketId,
  entryId,
  divisionName,
  bracketStatus,
  metadata,
  variant = 'default',
  blurVariant,
  onClick,
  canViewPicks = true
}) {
  const navigate = useNavigate()

  const hasPicks = !!entryId
  // Another player's picks stay hidden until the bracket locks (canViewPicks
  // is false only on someone else's profile while the bracket is still open).
  const picksHiddenUntilLock = !canViewPicks && !['locked', 'complete'].includes(bracketStatus)
  const isViewable = ['open', 'locked', 'complete'].includes(bracketStatus) && !picksHiddenUntilLock
  const canMakePicks = bracketStatus === 'open'
  const needsPicks = variant === 'needsPicks' || (canMakePicks && !hasPicks)

  const getChampionLabel = (status) => {
    if (!status || status === 'unknown' || status === 'not_selected') return null
    if (status === 'active') return 'Active'
    if (status === 'won') return 'Won'
    if (status === 'eliminated') return 'Eliminated'
    return null
  }

  const championLabel = metadata ? getChampionLabel(metadata.champion_status) : null
  const resolvedBlurVariant = blurVariant ?? (bracketId ? [...bracketId].reduce((acc, c) => acc + c.charCodeAt(0), 0) % 6 : 0)

  const handleClick = () => {
    if (onClick) {
      onClick()
      return
    }

    if (!isViewable) return

    if (entryId) {
      navigate(`/bracket/${bracketId}/picks?entryId=${entryId}`)
    } else {
      navigate(`/bracket/${bracketId}/picks`)
    }
  }

  const getActionText = () => {
    if (picksHiddenUntilLock) return 'Hidden until lock'
    if (!isViewable) return 'Coming soon'
    if (needsPicks) return 'Make Picks'
    return 'View Bracket'
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={!isViewable}
      data-blur-variant={needsPicks ? resolvedBlurVariant : undefined}
      className={`w-full p-3 sm:p-4 rounded-lg transition-all duration-200 ease-out text-left ${
        needsPicks
          ? 'card-accent-glow cursor-pointer'
          : 'card-glass-item card-glass-item-no-lift ' + (isViewable
            ? 'hover:shadow-lg cursor-pointer'
            : 'cursor-not-allowed opacity-60')
      }`}
    >
      {/* Mobile layout (stacked) */}
      <div className="sm:hidden">
        {/* Division name - full width */}
        <h4 className={`font-semibold mb-2 ${needsPicks ? 'text-[var(--color-ink)]' : ''}`}>
          {divisionName}
        </h4>

        {/* Status + Points row */}
        <div className="flex items-center justify-between mb-2">
          <p className={`text-sm capitalize ${needsPicks ? 'text-muted-foreground' : 'text-muted-foreground'}`}>
            {bracketStatus}
          </p>
          {hasPicks && metadata ? (
            <p className={`text-lg font-bold ${needsPicks ? 'text-[var(--color-ink)]' : 'text-primary'}`}>
              {metadata.total_points} {metadata.total_points === 1 ? 'point' : 'points'}
            </p>
          ) : (
            <span className={`text-sm font-medium ${needsPicks ? 'text-[var(--color-ink)]' : 'text-muted-foreground'}`}>
              {getActionText()}
            </span>
          )}
        </div>

        {/* Metrics row */}
        {hasPicks && metadata && (
          <EntryStatsRow
            potentialPoints={metadata.potential_points || 0}
            accuracy={metadata.accuracy}
            accuracyNullDisplay="zero"
            champion={championLabel ? { label: championLabel, status: metadata.champion_status, name: metadata.champion_team_name } : null}
            muted={needsPicks}
            wrap
          />
        )}
      </div>

      {/* Desktop layout (horizontal) */}
      <div className="hidden sm:flex sm:items-center sm:justify-between">
        {/* Left side - Division info */}
        <div className="flex-1 min-w-0">
          <h4 className={`font-semibold break-words ${needsPicks ? 'text-[var(--color-ink)]' : ''}`}>
            {divisionName}
          </h4>
          <p className={`text-sm capitalize ${needsPicks ? 'text-muted-foreground' : 'text-muted-foreground'}`}>
            {bracketStatus}
          </p>
        </div>

        {/* Right side - Stats or action text */}
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          {hasPicks && metadata ? (
            <>
              <p className={`text-lg font-bold whitespace-nowrap ${needsPicks ? 'text-[var(--color-ink)]' : 'text-primary'}`}>
                {metadata.total_points} {metadata.total_points === 1 ? 'point' : 'points'}
              </p>
              <EntryStatsRow
                potentialPoints={metadata.potential_points || 0}
                accuracy={metadata.accuracy}
                accuracyNullDisplay="zero"
                champion={championLabel ? { label: championLabel, status: metadata.champion_status, name: metadata.champion_team_name } : null}
                muted={needsPicks}
              />
            </>
          ) : (
            <span className={`text-sm font-medium ${needsPicks ? 'text-[var(--color-ink)]' : 'text-muted-foreground'}`}>
              {getActionText()}
            </span>
          )}
        </div>
      </div>
    </button>
  )
}
